import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getCollectionBySlug, CollectionInfo } from '@/lib/collections';
import { useAppNetwork } from '@/hooks/useAppNetwork';
import { fetchCollectionStats, fetchSandboxTraits, fetchSandboxFilteredTokens } from '@/lib/tzkt';
import { fetchCollectionTraits, fetchFilteredTokens, fetchDuplicateEmojis, SORT_OPTIONS, SortOption } from '@/lib/api';
import { CollectionToken, CollectionStats, TraitSummary, PaginationState } from '@/lib/types';
import { getCardImageUrl, getAnimatedThumbnailUrl, ipfsToHttp } from '@/lib/ipfs';
import EmojiTraitFilter from '@/components/EmojiTraitFilter';
import DuplicateEmojiFilter, { DuplicateFilterType } from '@/components/DuplicateEmojiFilter';
import { HeartButton } from '@/components/ui/HeartButton';
import { AuthToast } from '@/components/ui/AuthToast';
import { SweepModeToast } from '@/components/ui/SweepModeToast';
import { useWallet } from '@/hooks/useWallet';
import { useAuth } from '@/hooks/useAuth';
import { useFavorites } from '@/hooks/useFavorites';
import { useCart } from '@/context/CartContext';
import { fetchObjktListings } from '@/lib/objkt-api';
import type { ObjktListing } from '@/lib/types/objkt';

// Copy to clipboard component
function CopyableAddress({ address, label }: { address: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shortAddress = `${address.slice(0, 8)}...${address.slice(-4)}`;

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
      title={`Click to copy: ${address}`}
    >
      {label && <span className="text-gray-500 dark:text-gray-400">{label}</span>}
      <span className="text-gray-700 dark:text-gray-300">{shortAddress}</span>
      {copied ? (
        <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
}

interface NFTCardProps {
  token: CollectionToken;
  useAnimated?: boolean;
  isFavorited?: boolean;
  favoritesCount?: number;
  isFavoriteLoading?: boolean;
  onFavoriteClick?: () => void;
  // OBJKT listing support
  objktListing?: ObjktListing;
  isInCart?: boolean;
  onCartClick?: () => void;
}

function NFTCard({
  token,
  useAnimated = false,
  isFavorited = false,
  favoritesCount = 0,
  isFavoriteLoading = false,
  onFavoriteClick,
  objktListing,
  isInCart = false,
  onCartClick,
}: NFTCardProps) {
  // Use animated WebP for collections with animated NFTs (like Skeles)
  // This preserves animation while being 50-80% smaller than raw GIFs
  const imageUrl = useAnimated
    ? getAnimatedThumbnailUrl(token.metadata.image, 256)
    : getCardImageUrl(token.metadata.image);

  // Format price in tez
  const formatPrice = (mutez: string) => {
    const tez = Number(mutez) / 1_000_000;
    return tez % 1 === 0 ? tez.toFixed(0) : tez.toFixed(2);
  };

  // Determine if this token has a listing (native or OBJKT)
  const hasListing = token.listing || objktListing;
  const listingPrice = objktListing ? objktListing.priceMutez : token.listing?.price;
  const isObjktListing = !!objktListing;

  const handleCartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCartClick?.();
  };

  return (
    <Link
      to={`/nft/${token.tokenAddress}/${token.tokenId}`}
      className={`group block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow hover:shadow-lg transition-all ${
        isInCart
          ? 'ring-2 ring-blue-500 border-2 border-blue-500'
          : ''
      }`}
    >
      <div className="aspect-square bg-gray-200 dark:bg-gray-700 overflow-hidden relative">
        <img
          src={imageUrl}
          alt={token.metadata.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${token.tokenId}&size=300&background=random`;
          }}
        />
        {/* Heart button overlay */}
        {onFavoriteClick && (
          <div className="absolute top-2 right-2">
            <HeartButton
              isFavorited={isFavorited}
              favoritesCount={favoritesCount}
              isLoading={isFavoriteLoading}
              onClick={onFavoriteClick}
              size="md"
              showCount={favoritesCount > 0}
              className="bg-white/80 backdrop-blur-sm shadow-sm"
            />
          </div>
        )}
        {/* Price badge for listed items - upper left */}
        {hasListing && listingPrice && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-black/75 backdrop-blur-sm text-white text-sm font-bold rounded shadow">
            {formatPrice(listingPrice)} ꜩ
          </div>
        )}
        {/* Cart button for OBJKT listings */}
        {isObjktListing && onCartClick && (
          <button
            onClick={handleCartClick}
            className={`absolute bottom-2 right-2 p-2 rounded-lg shadow-md transition-all ${
              isInCart
                ? 'bg-green-500 text-white'
                : 'bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-200 hover:bg-blue-500 hover:text-white'
            } opacity-0 group-hover:opacity-100 focus:opacity-100`}
            aria-label={isInCart ? 'Remove from cart' : 'Add to cart'}
          >
            {isInCart ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            )}
          </button>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-medium text-gray-900 dark:text-white truncate">{token.metadata.name}</h3>
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">#{token.tokenId}</p>
          {hasListing && (
            <div className="flex items-center gap-1.5">
              {isObjktListing && (
                <div className="relative group/tooltip">
                  <img
                    src="https://objkt.com/favicon.ico"
                    alt="objkt.com"
                    className="w-4 h-4 rounded-sm"
                  />
                  {/* Tooltip */}
                  <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-10">
                    Listed on objkt.com
                    <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                  </div>
                </div>
              )}
              <p className="text-sm font-medium text-green-600 dark:text-green-400">For Sale</p>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

function PaginationControls({
  currentPage,
  totalPages,
  goToPage,
  goToPrevPage,
  goToNextPage,
  getPageNumbers,
  className = '',
}: {
  currentPage: number;
  totalPages: number;
  goToPage: (page: number) => void;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  getPageNumbers: () => (number | 'ellipsis')[];
  className?: string;
}) {
  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      <button
        onClick={goToPrevPage}
        disabled={currentPage === 1}
        className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ← Previous
      </button>
      <div className="flex items-center gap-1">
        {getPageNumbers().map((page, index) =>
          page === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-400 dark:text-gray-500">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                currentPage === page
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {page.toLocaleString()}
            </button>
          )
        )}
      </div>
      <button
        onClick={goToNextPage}
        disabled={currentPage === totalPages}
        className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next →
      </button>
    </div>
  );
}

export default function CollectionPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { network: appNetwork } = useAppNetwork();

  // Wallet and auth hooks for favorites
  const { wallet, address: walletAddress, connect, isLoading: isWalletLoading } = useWallet();
  const { isAuthenticated, authenticate, isAuthenticating } = useAuth(wallet, walletAddress);
  const { toggleFavorite, getStatus, isLoading: isFavoriteLoading, updateStatus } = useFavorites();

  const [collection, setCollection] = useState<CollectionInfo | null>(null);
  const [stats, setStats] = useState<CollectionStats | null>(null);
  const [tokens, setTokens] = useState<CollectionToken[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    offset: 0,
    limit: 24,
    hasMore: true,
    totalCount: 0,
  });
  const [traits, setTraits] = useState<TraitSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [indexStatus, setIndexStatus] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [sampleImage, setSampleImage] = useState<string>('');
  const [showAuthToast, setShowAuthToast] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [pendingFavorite, setPendingFavorite] = useState<{ contractAddress: string; tokenId: string } | null>(null);

  // OBJKT listings state
  const [objktListings, setObjktListings] = useState<Map<string, ObjktListing>>(new Map());
  const [, setObjktListingsLoading] = useState(false);

  // Cart context for OBJKT purchases
  const { addItem, removeItem, isInCart, itemCount, totalPriceXtz, openCart, items: cartItems } = useCart();

  // Parallax scroll effect for banner
  const bannerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!bannerRef.current) return;

      // Calculate how far user has scrolled relative to viewport height
      const scrollRange = window.innerHeight;
      const scrollY = window.scrollY;
      const progress = Math.min(scrollY / scrollRange, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Parse URL params for filters
  const parseTraitsFromUrl = useCallback((): Record<string, string[]> => {
    const traitsParam = searchParams.get('traits');
    if (!traitsParam) return {};
    try {
      const parsed = JSON.parse(decodeURIComponent(traitsParam));
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed;
      }
    } catch {
      // Invalid JSON, ignore
    }
    return {};
  }, [searchParams]);

  const parseDuplicateFilterFromUrl = useCallback((): { type: DuplicateFilterType; emojis: string[] } | null => {
    const dupType = searchParams.get('dup') as DuplicateFilterType | null;
    const dupEmojis = searchParams.get('dupEmojis');
    if (dupType && dupEmojis) {
      try {
        const emojis = JSON.parse(decodeURIComponent(dupEmojis));
        if (Array.isArray(emojis) && emojis.length > 0) {
          return { type: dupType, emojis };
        }
      } catch {
        // Invalid JSON, ignore
      }
    }
    return null;
  }, [searchParams]);

  // Initialize state from URL params
  const initialTraits = parseTraitsFromUrl();
  const initialSort = (searchParams.get('sort') as SortOption) || 'price_asc';
  const initialDupFilter = parseDuplicateFilterFromUrl();

  const [selectedTraits, setSelectedTraits] = useState<Record<string, string[]>>(initialTraits);
  const [sortBy, setSortBy] = useState<SortOption>(initialSort);
  const [duplicateFilter, setDuplicateFilter] = useState<{ type: DuplicateFilterType; emojis: string[] } | null>(initialDupFilter);

  // Sync URL when filters change
  const updateUrlWithFilters = useCallback((
    newTraits: Record<string, string[]>,
    newSort: SortOption,
    newDupFilter: { type: DuplicateFilterType; emojis: string[] } | null,
    newPage: number
  ) => {
    setSearchParams((prev) => {
      // Traits
      if (Object.keys(newTraits).length > 0) {
        prev.set('traits', encodeURIComponent(JSON.stringify(newTraits)));
      } else {
        prev.delete('traits');
      }

      // Sort (omit if default)
      if (newSort !== 'price_asc') {
        prev.set('sort', newSort);
      } else {
        prev.delete('sort');
      }

      // Duplicate filter
      if (newDupFilter && newDupFilter.emojis.length > 0) {
        prev.set('dup', newDupFilter.type);
        prev.set('dupEmojis', encodeURIComponent(JSON.stringify(newDupFilter.emojis)));
      } else {
        prev.delete('dup');
        prev.delete('dupEmojis');
      }

      // Page (omit if page 1)
      if (newPage > 1) {
        prev.set('page', String(newPage));
      } else {
        prev.delete('page');
      }

      return prev;
    });
  }, [setSearchParams]);

  // Handle favorite click - show toast if not authenticated, otherwise toggle
  const handleFavoriteClick = useCallback(async (contractAddress: string, tokenId: string) => {
    if (!walletAddress) {
      // No wallet connected - show toast
      setPendingFavorite({ contractAddress, tokenId });
      setShowAuthToast(true);
      return;
    }

    if (!isAuthenticated) {
      // Wallet connected but not signed in - show toast
      setPendingFavorite({ contractAddress, tokenId });
      setShowAuthToast(true);
      return;
    }

    // Already authenticated - toggle favorite directly
    try {
      await toggleFavorite(contractAddress, tokenId, true, async () => {});
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  }, [walletAddress, isAuthenticated, toggleFavorite]);

  // Handle sign in from toast
  const handleSignIn = useCallback(async () => {
    // If no wallet connected, connect first
    if (!walletAddress) {
      await connect();
      // After connect, walletAddress will update via state
      // The useEffect below will handle authentication once wallet is connected
      return;
    }

    // Wallet connected - authenticate
    const success = await authenticate();
    if (success) {
      // Show success state
      setAuthSuccess(true);

      // Toggle the favorite that was pending
      if (pendingFavorite) {
        try {
          await toggleFavorite(pendingFavorite.contractAddress, pendingFavorite.tokenId, true, async () => {});
        } catch (err) {
          console.error('Failed to toggle favorite:', err);
        }
        setPendingFavorite(null);
      }

      // Auto-dismiss after 2 seconds
      setTimeout(() => {
        setShowAuthToast(false);
        setAuthSuccess(false);
      }, 2000);
    }
  }, [walletAddress, connect, authenticate, pendingFavorite, toggleFavorite]);

  // Auto-authenticate after wallet connects if we have a pending favorite
  // Use a ref to track if we've already started auth to prevent loops
  const hasStartedAuth = useRef(false);

  useEffect(() => {
    // Reset the ref when toast is hidden
    if (!showAuthToast) {
      hasStartedAuth.current = false;
      return;
    }

    // Only auto-trigger if:
    // 1. Toast is showing
    // 2. Wallet just connected (walletAddress exists)
    // 3. Not already authenticated
    // 4. Not currently authenticating
    // 5. Haven't already started auth this session
    // 6. Have a pending favorite action
    if (walletAddress && !isAuthenticated && pendingFavorite && !isAuthenticating && !hasStartedAuth.current) {
      hasStartedAuth.current = true;
      handleSignIn();
    }
  }, [walletAddress, isAuthenticated, pendingFavorite, showAuthToast, isAuthenticating, handleSignIn]);

  // Handle dismiss toast
  const handleDismissAuthToast = useCallback(() => {
    setShowAuthToast(false);
    setPendingFavorite(null);
  }, []);

  // Update local favorite status from API response (tokens include favoritesCount and isFavorited)
  useEffect(() => {
    tokens.forEach((token) => {
      // The API returns favoritesCount and isFavorited on each token
      if (token.favoritesCount !== undefined || token.isFavorited !== undefined) {
        updateStatus(token.tokenAddress, token.tokenId, {
          favoritesCount: token.favoritesCount ?? 0,
          isFavorited: token.isFavorited ?? false,
        });
      }
    });
  }, [tokens, updateStatus]);

  // Load collection info and traits
  useEffect(() => {
    if (!slug) return;

    const collectionInfo = getCollectionBySlug(slug);
    console.log('[Collection] slug:', slug, 'collectionInfo:', collectionInfo);
    if (collectionInfo) {
      setCollection(collectionInfo);
      const isSandbox = appNetwork === 'sandbox';
      console.log('[Collection] appNetwork:', appNetwork, 'isSandbox:', isSandbox);

      // Load stats (sandbox will use direct RPC call)
      if (!isSandbox) {
        fetchCollectionStats(collectionInfo.address, 'mainnet').then(setStats);
      }

      // Load traits
      setIndexStatus('Loading traits...');
      if (isSandbox) {
        // Sandbox mode: fetch traits directly from contract via Taquito
        console.log('[Collection] Fetching sandbox traits for:', collectionInfo.address);
        fetchSandboxTraits(collectionInfo.address)
          .then((response) => {
            console.log('[Collection] Sandbox traits response:', response);
            setTraits(response.traits);
            setStats({
              address: collectionInfo.address,
              tokenCount: response.totalSupply,
              holderCount: 1, // Sandbox has single owner
              lastUpdated: new Date(),
            });
            setIndexStatus('');
          })
          .catch((err) => {
            console.error('[Collection] Failed to load sandbox traits:', err);
            setIndexStatus('Failed to load traits');
          });

        // Fetch sample image for banner
        fetchSandboxFilteredTokens(collectionInfo.address, { limit: 1 })
          .then((result) => {
            if (result.items.length > 0 && result.items[0].metadata?.image) {
              setSampleImage(getCardImageUrl(result.items[0].metadata.image));
            }
          })
          .catch(() => {});
      } else {
        // Production mode: use API server
        fetchCollectionTraits(collectionInfo.address)
          .then((response) => {
            setTraits(response.traits);
            setIndexStatus('');
          })
          .catch((err) => {
            console.error('Failed to load traits:', err);
            setIndexStatus('Failed to load traits');
          });

        // Fetch a sample NFT image for banner/avatar
        fetchFilteredTokens(collectionInfo.address, { limit: 1 })
          .then((result) => {
            if (result.items.length > 0 && result.items[0].metadata?.image) {
              setSampleImage(getCardImageUrl(result.items[0].metadata.image));
            }
          })
          .catch(() => {});
      }
    }
  }, [slug, appNetwork]);

  // Fetch OBJKT listings for the collection (only on mainnet)
  useEffect(() => {
    if (!collection) return;
    if (appNetwork === 'sandbox') return; // No OBJKT on sandbox

    const loadObjktListings = async () => {
      setObjktListingsLoading(true);
      try {
        // Fetch all OBJKT listings for this collection (paginated, get first 500)
        const allListings: ObjktListing[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore && page <= 10) { // Max 10 pages = 500 listings
          const response = await fetchObjktListings(collection.address, {
            page,
            pageSize: 50,
            sort: 'price_asc',
          });
          allListings.push(...response.listings);
          hasMore = response.pagination.hasMore;
          page++;
        }

        // Create a map of tokenId -> listing for quick lookup
        const listingsMap = new Map<string, ObjktListing>();
        for (const listing of allListings) {
          // If multiple listings for same token, keep the cheapest
          const existing = listingsMap.get(listing.tokenId);
          if (!existing || BigInt(listing.priceMutez) < BigInt(existing.priceMutez)) {
            listingsMap.set(listing.tokenId, listing);
          }
        }

        console.log('[Collection] Loaded', listingsMap.size, 'OBJKT listings');
        setObjktListings(listingsMap);
      } catch (err) {
        console.error('[Collection] Failed to fetch OBJKT listings:', err);
      } finally {
        setObjktListingsLoading(false);
      }
    };

    loadObjktListings();
  }, [collection?.address, appNetwork]);

  // Load tokens with server-side filtering for a specific page
  const loadPage = useCallback(async (
    collectionAddress: string,
    filters: Record<string, string[]>,
    page: number,
    sort: SortOption,
    dupFilter: { type: DuplicateFilterType; emojis: string[] } | null
  ) => {
    setIsLoading(true);

    const limit = 24;
    const offset = (page - 1) * limit;
    const isSandbox = appNetwork === 'sandbox';

    try {
      // Sandbox mode: use direct Taquito fetch
      if (isSandbox) {
        console.log('[loadPage] Sandbox mode - fetching tokens for:', collectionAddress);
        const result = await fetchSandboxFilteredTokens(collectionAddress, {
          offset,
          limit,
          sort,
          traits: filters,
        });
        console.log('[loadPage] Sandbox tokens result:', result);

        setTokens(result.items);
        setPagination(result.pagination);
        return;
      }

      // Production mode: use API server
      // Check if duplicate filter is active (requires emojis to be selected)
      const isDuplicateFilterActive = dupFilter && dupFilter.emojis.length > 0;

      if (isDuplicateFilterActive) {
        // Use duplicate emoji endpoint
        const result = await fetchDuplicateEmojis(collectionAddress, {
          minCount: dupFilter.type === 'triples' ? 3 : 2,
          minTypes: dupFilter.type === 'double-doubles' ? 2 : 1,
          emojis: dupFilter.emojis,
          offset,
          limit,
        });

        setTokens(result.items);
        setPagination({
          offset: result.pagination.offset,
          limit: result.pagination.limit,
          hasMore: result.pagination.hasMore,
          totalCount: result.pagination.totalCount,
        });
      } else {
        // Use regular filtered tokens endpoint
        const result = await fetchFilteredTokens(collectionAddress, {
          offset,
          limit,
          sort,
          traits: filters,
        });

        // If no tokens found but we have OBJKT listings, create synthetic tokens from listings
        if (result.items.length === 0 && result.pagination.totalCount === 0) {
          console.log('[loadPage] No TzKT tokens, will use OBJKT listings directly');
          // Don't set tokens here - let the OBJKT-only display handle it
          setTokens([]);
          setPagination({ offset: 0, limit, hasMore: false, totalCount: 0 });
        } else {
          setTokens(result.items);
          setPagination(result.pagination);
        }
      }
    } catch (error) {
      console.error('Failed to load tokens:', error);
    } finally {
      setIsLoading(false);
    }
  }, [appNetwork]);

  // Current page (1-indexed) - initialize from URL param
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const [currentPage, setCurrentPage] = useState(initialPage > 0 ? initialPage : 1);

  // Sync page state with URL param on mount and when URL changes externally
  useEffect(() => {
    const pageParam = parseInt(searchParams.get('page') || '1', 10);
    const validPage = pageParam > 0 ? pageParam : 1;
    if (validPage !== currentPage) {
      setCurrentPage(validPage);
    }
  }, [searchParams]);

  // Load tokens when collection, filters, sort, or page change
  useEffect(() => {
    if (collection) {
      loadPage(collection.address, selectedTraits, currentPage, sortBy, duplicateFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection?.address, selectedTraits, currentPage, sortBy, duplicateFilter]);

  // Clear trait filters when duplicate filter is active (they're mutually exclusive)
  const handleDuplicateFilterChange = (filter: { type: DuplicateFilterType; emojis: string[] } | null) => {
    const newTraits = filter && filter.emojis.length > 0 ? {} : selectedTraits;
    if (filter && filter.emojis.length > 0) {
      setSelectedTraits({}); // Clear regular trait filters
    }
    setDuplicateFilter(filter);
    setCurrentPage(1);
    updateUrlWithFilters(newTraits, sortBy, filter, 1);
  };

  // Clear duplicate filter when trait filters are used
  const handleTraitToggle = (traitName: string, value: string) => {
    const newDupFilter = duplicateFilter ? null : duplicateFilter;
    if (duplicateFilter) {
      setDuplicateFilter(null); // Clear duplicate filter
    }
    setSelectedTraits((prev) => {
      const current = prev[traitName] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      let newTraits: Record<string, string[]>;
      if (next.length === 0) {
        const { [traitName]: _, ...rest } = prev;
        newTraits = rest;
      } else {
        newTraits = { ...prev, [traitName]: next };
      }

      // Update URL with new traits
      setCurrentPage(1);
      updateUrlWithFilters(newTraits, sortBy, newDupFilter, 1);

      return newTraits;
    });
  };

  // Handle sort change
  const handleSortChange = (newSort: SortOption) => {
    setSortBy(newSort);
    setCurrentPage(1);
    updateUrlWithFilters(selectedTraits, newSort, duplicateFilter, 1);
  };

  // Handle clear all filters
  const handleClearAll = () => {
    setSelectedTraits({});
    setDuplicateFilter(null);
    setCurrentPage(1);
    updateUrlWithFilters({}, sortBy, null, 1);
  };

  const hasActiveFilters = Object.keys(selectedTraits).length > 0 || duplicateFilter !== null;
  const totalCount = pagination.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pagination.limit) || 1;

  // Get listings on current page for sweep mode
  const pageListings = useMemo(() => {
    const listings: ObjktListing[] = [];
    for (const token of tokens) {
      const listing = objktListings.get(token.tokenId);
      if (listing) {
        listings.push(listing);
      }
    }
    return listings;
  }, [tokens, objktListings]);

  // Get set of listing IDs currently in cart
  const cartListingIds = useMemo(() => {
    return new Set(cartItems.map((item) => item.listingId));
  }, [cartItems]);

  // Handle adding listing to cart from sweep
  const handleSweepAddToCart = useCallback((listing: ObjktListing) => {
    addItem({
      listingId: listing.id,
      bigmapKey: listing.bigmapKey,
      source: 'objkt',
      faContract: listing.faContract,
      tokenId: listing.tokenId,
      priceMutez: listing.priceMutez,
      priceXtz: listing.priceXtz,
      seller: listing.seller,
      marketplaceContract: listing.marketplaceContract,
      tokenName: listing.token.name || undefined,
      displayUri: listing.token.displayUri || undefined,
      thumbnailUri: listing.token.thumbnailUri || undefined,
    });
  }, [addItem]);

  // Navigation handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      updateUrlWithFilters(selectedTraits, sortBy, duplicateFilter, page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Generate page numbers to display
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  if (!collection) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Collection not found</h1>
        <Link to="/" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
          ← Back to collections
        </Link>
      </div>
    );
  }

  // Convert IPFS URLs to HTTP and prefer collection thumbnail over sample NFT
  const thumbnailUrl = collection.thumbnail ? ipfsToHttp(collection.thumbnail) : '';
  // Use the same image for both banner and avatar (thumbnail preferred)
  const avatarImage = thumbnailUrl || sampleImage;
  const bannerImage = avatarImage;
  const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(collection.name)}&size=128&background=6366f1&color=fff&bold=true`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="relative">
        {/* Banner with Parallax */}
        <div ref={bannerRef} className="h-48 md:h-64 bg-gradient-to-r from-indigo-500 to-purple-600 overflow-hidden relative">
          {bannerImage && (
            <img
              src={bannerImage}
              alt={collection.name}
              className="absolute w-full opacity-40"
              style={{
                height: '150%',
                objectFit: 'cover',
                top: `${-scrollProgress * 50}%`,
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
        </div>

        {/* Collection Info */}
        <div className="container mx-auto px-4">
          <div className="relative -mt-16 flex flex-col md:flex-row gap-4 pb-6">
            {/* Avatar */}
            <div className="w-32 h-32 rounded-xl border-4 border-white dark:border-gray-800 shadow-lg overflow-hidden bg-white dark:bg-gray-800 flex-shrink-0">
              <img
                src={avatarImage || fallbackUrl}
                alt={collection.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = fallbackUrl;
                }}
              />
            </div>

            {/* Info - use padding-top to position title at banner bottom (half of avatar height since avatar straddles) */}
            <div className="flex-1 min-w-0 md:pt-6">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">{collection.name}</h1>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl">{collection.description}</p>
              <div className="mt-2">
                <CopyableAddress address={collection.address} label="Contract:" />
              </div>
            </div>

            {/* Stats */}
            {stats && (
              <div className="flex gap-6 bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-3 md:mt-8 flex-shrink-0 h-fit">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.tokenCount.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Items</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Filters - Desktop */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            {/* Duplicate Emoji Filter - Only for Skeles */}
            {collection.slug === 'skeles' && (
              <DuplicateEmojiFilter
                collectionAddress={collection.address}
                onFilterChange={handleDuplicateFilterChange}
                activeFilter={duplicateFilter}
              />
            )}
            <EmojiTraitFilter
              traits={traits}
              selectedTraits={selectedTraits}
              onTraitToggle={handleTraitToggle}
              onClearAll={handleClearAll}
              isLoading={indexStatus.includes('Loading')}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow text-sm font-medium dark:text-gray-200"
                >
                  Filters {Object.keys(selectedTraits).length > 0 && `(${Object.keys(selectedTraits).length})`}
                </button>
                <span className="text-gray-600 dark:text-gray-400">
                  {hasActiveFilters ? (
                    <>
                      {totalCount.toLocaleString()} {totalCount === 1 ? 'match' : 'matches'}
                    </>
                  ) : (
                    <>
                      {tokens.length.toLocaleString()} of {totalCount.toLocaleString()} items
                    </>
                  )}
                </span>
              </div>

              <div className="flex items-center gap-4">
                {/* Sort dropdown */}
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value as SortOption)}
                  className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {/* Pagination info */}
                {totalCount > 0 && (
                  <span className="text-sm text-gray-400">
                    Page {currentPage} of {totalPages.toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {/* Mobile Filters */}
            {showFilters && (
              <div className="lg:hidden mb-6">
                {/* Duplicate Emoji Filter - Only for Skeles */}
                {collection.slug === 'skeles' && (
                  <DuplicateEmojiFilter
                    collectionAddress={collection.address}
                    onFilterChange={handleDuplicateFilterChange}
                    activeFilter={duplicateFilter}
                  />
                )}
                <EmojiTraitFilter
                  traits={traits}
                  selectedTraits={selectedTraits}
                  onTraitToggle={handleTraitToggle}
                  onClearAll={handleClearAll}
                  isLoading={indexStatus.includes('Loading')}
                />
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && !isLoading && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                goToPage={goToPage}
                goToPrevPage={goToPrevPage}
                goToNextPage={goToNextPage}
                getPageNumbers={getPageNumbers}
                className="mb-6"
              />
            )}

            {/* Grid */}
            {isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow animate-pulse">
                    <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
                    <div className="p-3">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : tokens.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <p>No items match your filters</p>
                {hasActiveFilters && (
                  <button
                    onClick={handleClearAll}
                    className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {tokens.map((token) => {
                    const status = getStatus(token.tokenAddress, token.tokenId);
                    const objktListing = objktListings.get(token.tokenId);
                    const inCart = objktListing ? isInCart(objktListing.id) : false;

                    const handleCartClick = () => {
                      if (!objktListing) return;
                      if (inCart) {
                        removeItem(objktListing.id);
                      } else {
                        addItem({
                          listingId: objktListing.id,
                          bigmapKey: objktListing.bigmapKey, // On-chain ask_id
                          source: 'objkt',
                          faContract: objktListing.faContract,
                          tokenId: objktListing.tokenId,
                          priceMutez: objktListing.priceMutez,
                          priceXtz: objktListing.priceXtz,
                          seller: objktListing.seller,
                          marketplaceContract: objktListing.marketplaceContract,
                          tokenName: objktListing.token.name || undefined,
                          displayUri: objktListing.token.displayUri || undefined,
                          thumbnailUri: objktListing.token.thumbnailUri || undefined,
                        });
                      }
                    };

                    return (
                      <NFTCard
                        key={`${token.tokenAddress}-${token.tokenId}`}
                        token={token}
                        useAnimated={collection.hasAnimated === true}
                        isFavorited={status?.isFavorited ?? token.isFavorited ?? false}
                        favoritesCount={status?.favoritesCount ?? token.favoritesCount ?? 0}
                        isFavoriteLoading={isFavoriteLoading(token.tokenAddress, token.tokenId)}
                        onFavoriteClick={() => handleFavoriteClick(token.tokenAddress, token.tokenId)}
                        objktListing={objktListing}
                        isInCart={inCart}
                        onCartClick={objktListing ? handleCartClick : undefined}
                      />
                    );
                  })}
                </div>

                {/* Pagination Controls - Bottom */}
                {totalPages > 1 && (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    goToPage={goToPage}
                    goToPrevPage={goToPrevPage}
                    goToNextPage={goToNextPage}
                    getPageNumbers={getPageNumbers}
                    className="mt-8"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Auth Toast */}
      <AuthToast
        isVisible={showAuthToast}
        isAuthenticating={isAuthenticating}
        isSuccess={authSuccess}
        isWalletConnected={!!walletAddress}
        isWalletLoading={isWalletLoading}
        onSignIn={handleSignIn}
        onDismiss={handleDismissAuthToast}
      />

      {/* Sweep Mode Toast - shown when items are in cart */}
      <SweepModeToast
        isVisible={itemCount > 0 && !showAuthToast}
        itemCount={itemCount}
        totalPriceXtz={totalPriceXtz}
        pageListings={pageListings}
        cartListingIds={cartListingIds}
        onAddToCart={handleSweepAddToCart}
        onRemoveFromCart={removeItem}
        onOpenCart={openCart}
      />
    </div>
  );
}
